import React, { memo, useCallback, useMemo, useState } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useForm } from "react-hook-form";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../../components/loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { showModal } from "../../app/store/slice/counterSlice";
import ImageUpload from "../../components/form-components/ImageUpload";
import AddIcon from "@mui/icons-material/Add";
import { Alert } from "@mui/material";
import { FormInputText } from "../../components/form-components/FormInputText";
import { motion } from "motion/react";

const CreateEditDialog = React.memo(
  ({ open, handleClose, control, errors, handleSubmit, onSubmit }) => {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth={"sm"}>
        <DialogTitle className="text-primary">New Banner</DialogTitle>
        <DialogContent>
          <Grid container columnSpacing={{ xs: 5 }}>
            <Grid item xs={12}>
              <FormInputText
                name="banner_name"
                control={control}
                label="Banner Name"
                errors={errors}
                mandatory="true"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="error">
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

// ✅ BannerItem wrapper so useMemo can be used per banner (hooks can't be in .map())
// ✅ This stabilizes routeValue so ImageUpload useEffect doesn't re-fire every render
const BannerItem = memo(
  ({ banner, platform, control, loadInitialData, regionId, routeItems }) => {
    const routeValue = useMemo(
      () => ({
        route_type: banner?.route_type,
        route_item: banner?.route_item,
        banner_from_date: banner?.banner_from_date,
        banner_to_date: banner?.banner_to_date,
      }),
      [
        banner?.route_type,
        banner?.route_item,
        banner?.banner_from_date,
        banner?.banner_to_date,
      ],
    );

    return (
      <Grid item xs={12} md={4} lg={4} xl={4}>
        <ImageUpload
          status={banner?.active_status}
          name={banner?.id?.toString()}
          control={control}
          label={banner?.banner_name}
          type="text"
          defaultValue={
            platform == "app" ? banner?.banner_image : banner?.web_image
          }
          loadInitialData={loadInitialData}
          regionId={regionId || banner?.region_id}
          routeList={routeItems}
          routeValue={routeValue}
          platform={platform}
        />
      </Grid>
    );
  },
);

function BannerUpload({
  regionId,
  loadInitialData,
  routeItems,
  formData,
  loader,
  setLoader,
}) {
  const dispatch = useDispatch();
  const platform = useSelector((state) => state.counter?.platform);
  const [bannerDates, setBannerDates] = useState({});
  const [open, setOpen] = useState(false);

  const methods = useForm();
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = methods;

  const onSubmit = (data) => save(data);

  const save = async (data) => {
    setLoader(true);
    try {
      const payload = { ...data, region_id: regionId };
      const response = await axios.post("/banner", payload);
      if (!response?.data?.error) {
        toast.success(response?.data?.message);
        loadInitialData();
        reset();
      } else {
        let errDesc = "";
        if (response?.data?.data) {
          Object.values(response?.data?.data).map((error, index) => {
            errDesc =
              index === 0 ? errDesc + error + "\n " : errDesc + error + "\n ";
          });
        } else {
          errDesc = response?.data?.message;
        }
        dispatch(showModal({ msg: errDesc, error: response?.data?.error }));
      }
    } catch (error) {
      dispatch(showModal({ msg: error?.message, error: true }));
    } finally {
      setOpen(false);
      setLoader(false);
    }
  };

  const handleClose = useCallback(() => setOpen(false), []);

  const bannersBasedOnPlatform = (formData?.length > 0 && formData) || [];

  return loader ? (
    <Loader />
  ) : (
    <>
      <Grid item xs={12}>
        <ul
          className="w-100 d-inline-flex justify-content-around"
          style={{ color: "red", marginTop: "8px" }}
        >
          {formData?.length <= 8 && (
            <Button
              size="small"
              variant="contained"
              onClick={() => setOpen(true)}
              icon={<AddIcon />}
            >
              New Banner
            </Button>
          )}
          <li>Please upload only jpg, jpeg, png.</li>
          <li>File size shall not exceed 300kb</li>
          <li>Dimension : {platform == "app" ? "2 :1 ratio" : "16:9"}</li>
        </ul>
      </Grid>
      <Grid
        container
        spacing={2}
        columnSpacing={{ xs: 5 }}
        style={{ display: "flex" }}
      >
        {bannersBasedOnPlatform?.length > 0 ? (
          bannersBasedOnPlatform?.map((banner) => (
            // ✅ Use banner.id as key (not index) so React doesn't remount on reorder
            <BannerItem
              key={banner?.id}
              banner={banner}
              platform={platform}
              control={control}
              loadInitialData={loadInitialData}
              regionId={regionId}
              routeItems={routeItems}
            />
          ))
        ) : (
          <Grid item xs={12}>
            <Alert severity="warning">No Banners Found !!</Alert>
          </Grid>
        )}
      </Grid>
      <CreateEditDialog
        open={open}
        handleClose={handleClose}
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
      />
    </>
  );
}

export default BannerUpload;
